/**
 * Trade completion writer (b19-trade-plan-to-done).
 *
 * Shared logic behind the Trade card "Done" button. Completing a trade marks
 * BOTH sides as caught in the user's Dex:
 *   - the species they gave away (outgoing)
 *   - the species they received (incoming)
 *
 * Both land as `source: "trade"` rows in dex_records via the existing manual
 * mark-caught path (`markTradeCaught` -> dexStateStore -> debounced PUT
 * /api/dex/state). That is the same write pipeline the "M" manual badge uses,
 * so no new endpoint or persistence primitive is introduced: the new green "T"
 * badge renders on these rows in the Pokedex grid.
 *
 * A trade is a species-level acquisition, so each side is written against the
 * MAIN ("pokemon") variant. The store's cascade rules already mark MAIN present
 * for any cascading-variant catch, so MAIN is the correct, minimal target.
 *
 * The notification side (in-app toast + native local notification) is injected
 * so this module stays free of React/Capacitor imports and is unit-testable as
 * pure logic. Callers pass the existing `toast` from use-toast and the existing
 * `fireLocalNotification` from lib/notifications.
 */
import { markTradeCaught } from "./dexStateStore";

export interface TradeCompletionInput {
  /** National Dex number of the species the user gave away. */
  outgoingSpeciesId: number | null;
  /** National Dex number of the species the user received. */
  incomingSpeciesId: number | null;
  /** Display name for the outgoing species (for the notification copy). */
  outgoingSpeciesName: string | null;
  /** Display name for the incoming species (for the notification copy). */
  incomingSpeciesName: string | null;
}

export interface TradeCompletionDeps {
  /** In-app toast (use-toast's `toast`). */
  toast: (opts: { title: string; description?: string }) => void;

}

export interface TradeCompletionResult {
  /** dex_records writes attempted for present, distinct species. */
  written: number;
}

/**
 * Mark both sides of a completed trade as caught and fire the confirmation
 * notification (toast + push). Returns how many dex rows were written.
 *
 * Idempotent at the store layer: re-running for an already-present trade row is
 * a no-op (see markTradeCaught). A prior manual or scan row IS re-stamped to
 * trade provenance: under last-write-wins the completed trade is simply the
 * newest statement about the cell (the store's contract; this doc used to
 * claim manual rows were never overwritten, which stopped being true with the
 * 2026-07-18 LWW change - 2026-08-30 audit).
 */
export function markTradeCompletion(
  input: TradeCompletionInput,
  deps: TradeCompletionDeps,
): TradeCompletionResult {
  let written = 0;

  // De-duplicate so a same-species swap writes the row once.
  const ids = new Set<number>();
  for (const id of [input.outgoingSpeciesId, input.incomingSpeciesId]) {
    if (typeof id === "number" && Number.isFinite(id) && id >= 1) ids.add(id);
  }
  for (const id of Array.from(ids)) {
    if (markTradeCaught("pokemon", id)) written += 1;
  }

  const speciesA = input.outgoingSpeciesName ?? "your Pokemon";
  const speciesB = input.incomingSpeciesName ?? "their Pokemon";
  const body = `Marked ${speciesA} and ${speciesB} as caught.`;

  // A toast only (2026-09-11): the trainer just marked this trade done
  // themselves, so an OS banner about it would tell them nothing new.
  deps.toast({ title: "Trade complete", description: body });

  return { written };
}

